import React from "react";
import LegalLayout from "./LegalLayout";
import { useTheme } from "../../context/ThemeContext";
import { codProvinces, codMinAmount, formatProvinceList, freeShippingProvinces, zoneCapsForProvince } from "../../utils/shipping";
import { comuniListText } from "../../utils/comuni";

const TermsAndConditions = () => {
  const { settings } = useTheme();
  const codList = formatProvinceList(codProvinces(settings)) || "—";
  const codMin = codMinAmount(settings);
  const freeShipThreshold = Number(settings?.commerce?.free_shipping_threshold || 0);
  const codCondition = codMin > 0 ? `su ordini superiori a ${codMin.toFixed(2)} €` : "senza importo minimo";
  const freeShipCondition = freeShipThreshold > 0 ? `> ${freeShipThreshold.toFixed(0)} €` : "qualsiasi importo";
  const freeShipComuniCaps = [...new Set(freeShippingProvinces(settings).flatMap((p) => zoneCapsForProvince(p, settings)))];
  const freeShipComuniText = comuniListText(freeShipComuniCaps) || "Siracusa e Catania";

  return (
  <LegalLayout
    testid="page-terms"
    eyebrow="Documento legale"
    title="Termini e Condizioni di Vendita"
    updated="Ottobre 2026"
  >
    <p>
      I presenti Termini e Condizioni disciplinano l'utilizzo del sito
      <strong> LEOVERA Nutrition</strong> e i contratti di vendita a distanza conclusi tra
      LEOVERA e il cliente (di seguito "Cliente") in conformità al D.lgs. 206/2005 ("Codice del
      Consumo") e al D.lgs. 70/2003 in materia di commercio elettronico.
    </p>

    <h2>1. Informazioni sul venditore</h2>
    <p>
      Venditore: <strong>LEOVERA Nutrition di Davide Allibrio</strong>, P.IVA 02155960897,
      REA SR-472359, Sede legale: Viale Tunisi 68, Siracusa, PEC: d.allibriopec@pec.it.
      Contatti: <strong>info@leoveranutrition.it</strong>.
      Il servizio clienti è disponibile tramite l'assistente conversazionale "Leo" all'interno
      del sito.
    </p>

    <h2>2. Prodotti e informazioni</h2>
    <p>
      I prodotti offerti sono integratori alimentari di marchi certificati (Anderson, Eurosup,
      Prolabs e altri partner). Le immagini e le descrizioni hanno finalità puramente
      illustrative: eventuali variazioni di packaging o formulazione da parte del produttore
      non costituiscono difetto di conformità.
    </p>

    <h2>3. Prezzi e acquisto</h2>
    <p>
      I prezzi dei prodotti, per ciascun formato e variante disponibile, sono indicati sul sito
      prima dell'acquisto. Il contratto di vendita si conclude nel momento in cui il pagamento
      viene autorizzato con successo tramite <strong>Stripe</strong> al termine del checkout,
      oppure, per gli ordini in contrassegno, al momento della conferma dell'ordine da parte di
      LEOVERA. L'assistente virtuale "Leo" è un canale di assistenza e consulenza opzionale,
      utile per scegliere i prodotti più adatti alle proprie esigenze, ma non è necessario per
      completare un acquisto: il Cliente può selezionare i prodotti e pagare direttamente dal
      carrello in qualsiasi momento.
    </p>

    <h2>4. Modalità di pagamento</h2>
    <ul>
      <li>Carte di credito/debito tramite <strong>Stripe</strong> (protocollo TLS, PCI-DSS).</li>
      <li>PayPal e altri metodi elettronici indicati in fase di checkout.</li>
      <li>Contanti alla consegna (COD): disponibile solo per le province di <strong>{codList}</strong> {codCondition}.</li>
    </ul>

    <h2>5. Spedizioni e consegne</h2>
    <p>
      Le spedizioni avvengono in 3-5 giorni lavorativi in tutta Italia tramite corriere
      espresso. La spedizione è <strong>gratuita</strong> per gli ordini {freeShipCondition} esclusivamente
      nei comuni di {freeShipComuniText} (non per l'intera provincia). Il rischio di perimento
      della merce si trasferisce al Cliente al momento della consegna.
    </p>

    <h2>6. Diritto di recesso</h2>
    <p>
      Ai sensi degli artt. 52-59 del Codice del Consumo, il Cliente consumatore ha diritto di
      recedere entro <strong>14 giorni</strong> dalla ricezione dei prodotti, senza dover
      indicare alcuna motivazione. Il recesso si esercita inviando una comunicazione scritta a
      <strong> info@leoveranutrition.it</strong>. I prodotti dovranno essere restituiti integri, nella
      confezione originale sigillata, entro 14 giorni dalla comunicazione di recesso.
    </p>
    <p>
      Ai sensi dell'art. 59 lett. e) del Codice del Consumo, <strong>il diritto di recesso non
      si applica</strong> a prodotti sigillati che non si prestano ad essere restituiti per
      motivi igienici o connessi alla protezione della salute e che sono stati aperti dopo la
      consegna.
    </p>
    <p>
      Per esercitare il recesso puoi{" "}
      <a href="/recesso" data-testid="terms-recesso-online-link">recedere dal contratto qui</a> tramite
      il modulo online (riceverai subito un'email di ricevuta), oppure utilizzare il nostro{" "}
      <a href="/modulo-recesso">modulo di recesso scaricabile</a> (facoltativo ma consigliato).
    </p>

    <h2>7. Garanzia legale di conformità</h2>
    <p>
      Ai prodotti si applica la garanzia legale di conformità di 24 mesi prevista dagli
      artt. 128 e seguenti del Codice del Consumo.
    </p>
    <p>
      <strong>Prodotti danneggiati, con sigillo rotto o non conformi.</strong> L'apertura del
      sigillo per verificare il prodotto alla consegna non pregiudica la garanzia legale di
      conformità, che resta valida per 24 mesi dalla consegna ai sensi degli artt. 128-135 del
      Codice del Consumo: in presenza di un difetto di conformità già esistente al momento della
      consegna, il Cliente deve denunciarlo entro 2 mesi dalla scoperta e ha diritto, a sua scelta
      e senza spese, alla riparazione o alla sostituzione del prodotto, salvo che il rimedio
      richiesto sia impossibile o eccessivamente oneroso; in mancanza, può richiedere una
      riduzione del prezzo o la risoluzione del contratto. Diversamente, ai sensi dell'art. 59,
      lett. e) del Codice del Consumo, il diritto di recesso non si applica ai prodotti sigillati,
      per motivi igienici o di protezione della salute, una volta aperti dopo la consegna. In caso
      di prodotti arrivati danneggiati durante il trasporto, il Cliente è invitato a segnalarlo
      entro 48 ore dalla consegna scrivendo a <strong>info@leoveranutrition.it</strong> con foto
      del prodotto e dell'imballaggio, per consentirci di avviare la sostituzione o il rimborso nel
      più breve tempo possibile.
    </p>

    <h2>8. Programma fedeltà "Cashback Elite"</h2>
    <p>
      Ad ogni consegna confermata il Cliente registrato accumula punti nella misura di
      <strong> 2 punti ogni 10 € di prodotto consegnato</strong>. I punti sono personali,
      non cedibili e possono essere convertiti in sconti secondo le regole pubblicate nel proprio
      account.
    </p>

    <h2>9. Disclaimer sugli integratori alimentari</h2>
    <p>
      Gli integratori alimentari <strong>non sono medicinali</strong> e non intendono
      diagnosticare, curare o prevenire alcuna malattia. Non vanno intesi come sostituti di una
      dieta variata ed equilibrata e di uno stile di vita sano. Prima dell'assunzione, in caso
      di gravidanza, allattamento, patologie in corso o assunzione di farmaci, consultare
      il proprio medico curante. Tenere fuori dalla portata dei bambini al di sotto dei 3 anni.
    </p>

    <h2>10. Legge applicabile e foro competente</h2>
    <p>
      I presenti Termini sono regolati dalla legge italiana. Per il Cliente consumatore è
      competente in via esclusiva il foro del luogo di residenza o domicilio elettivo, ai sensi
      dell'art. 66-bis del Codice del Consumo.
    </p>

    <h2>11. Risoluzione delle controversie (ODR)</h2>
    <p>
      Ai sensi del Reg. (UE) 524/2013 il Cliente consumatore può accedere alla piattaforma
      europea per la risoluzione online delle controversie:{" "}
      <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">
        ec.europa.eu/consumers/odr
      </a>
      .
    </p>
  </LegalLayout>
  );
};

export default TermsAndConditions;
